import {
  AnchorJob,
  AnchorJobSummary,
  AnchorTask,
  GamebookPlayLite,
} from "../service";
import { GamebookPlay } from "../../gamebook/service";

/** Reviews assumed everywhere else in this file — every fixture job predates the `reviewMode`/
 *  `band` contract, so they're all plain Films Ground jobs. */
const FILMS_GROUND_REVIEW = { reviewMode: "films_ground" as const, band: null };

/**
 * Fixture data for developing/validating the two Films Ground screens before the backend routes
 * exist (plan Stage 5/6 build against Stage 1-4's real routes with SEEDED data — this file is
 * that seed, standing in until those stages land). Values are lifted from the two mockups
 * (`films-ground-set-start-mockup.html`'s `STARTPLAYS`, `films-ground-anchor-review-mockup.html`'s
 * `WINDOWS`) and adapted onto the plan's actual data model (§4.1-4.2), not the original spec's
 * simpler `AnchorJob`/`AnchorTask` shape — the plan supersedes the spec for anything it re-derives.
 *
 * Enable with `REACT_APP_FILMS_GROUND_FIXTURES=1` (see `../service.ts`). Once on, every fixture
 * job below shows up as its own row in Control Room's rail (`controlRoom/service.ts`'s
 * `getControlRoomJobs` builds fixture `ControlRoomJob`s off `listFixtureJobSummaries()` below when
 * this flag is set — Addison: "can you just make the fixture show in the sidebar so I don't have
 * to go through this?", i.e. a pre-authed link straight to `/spotter/control-room/:jobId`), so
 * clicking a row is the normal way in now; a direct `/spotter/control-room/:jobId` URL still works
 * too. One job per distinct `AnchorJobStatus` × `AnchorJobMode` combination the UI actually renders
 * differently:
 *
 *   fg_A014      films_ground  awaiting_start      — Phase A start picker, no anchors yet
 *   fg_A013      films_ground  analyzing           — polling/pending, no anchors yet
 *   fg_A012      films_ground  needs_confirmation  — the full anchor review carousel
 *   fg_A015      films_ground  submitted           — terminal, `SubmitBlock`'s success banner
 *   fg_band_001  films_ground  needs_confirmation  — slowmo_band review surface (mixed open/resolved)
 *   tf_B001      top_films     awaiting_start      — same Phase A picker, top_films mode
 *   tf_B002      top_films     submitted           — empty anchors[], `AnchorReviewPanel`'s
 *                                                     "auto-submitted" status card (no review UI)
 */

export function filmsGroundFixturesEnabled(): boolean {
  return process.env.REACT_APP_FILMS_GROUND_FIXTURES === "1";
}

/** P001-P010 — lifted verbatim from `films-ground-set-start-mockup.html`'s `STARTPLAYS`. */
export const FIXTURE_FIRST_TEN_PLAYS: GamebookPlayLite[] = [
  {
    uid: "P001",
    quarter: 1,
    summary: "Kickoff — NYJ 2 T-Bass to CIN 22, returned to CIN 24",
  },
  {
    uid: "P002",
    quarter: 1,
    summary: "9 C-Ward pass short left to 84 G-Helm for 8 yds",
  },
  { uid: "P003", quarter: 1, summary: "30 C-Brown up the middle for 3 yds" },
  {
    uid: "P004",
    quarter: 1,
    summary: "9 C-Ward pass short right to 1 J-Chase for 12 yds",
  },
  { uid: "P005", quarter: 1, summary: "30 C-Brown off left tackle for 5 yds" },
  {
    uid: "P006",
    quarter: 1,
    summary: "9 C-Ward pass incomplete deep right to 5 T-Higgins",
  },
  {
    uid: "P007",
    quarter: 1,
    summary: "9 C-Ward pass short middle to 84 G-Helm for 9 yds",
  },
  {
    uid: "P008",
    quarter: 1,
    summary: "9 C-Ward pass deep right to 5 T-Higgins for 24 yds",
  },
  { uid: "P009", quarter: 1, summary: "30 C-Brown up the middle for 2 yds" },
  {
    uid: "P010",
    quarter: 1,
    summary: "9 C-Ward pass short left to 1 J-Chase for 7 yds",
  },
];

/** ±4 gamebook windows for the 5 anchors the mockup ships PENDING — lifted verbatim from
 *  `films-ground-anchor-review-mockup.html`'s `WINDOWS`. */
const PENDING_WINDOWS: Record<string, GamebookPlayLite[]> = {
  P031: [
    {
      uid: "P027",
      quarter: 2,
      summary: "9 C-Ward pass incomplete to 5 T-Higgins",
    },
    { uid: "P028", quarter: 2, summary: "30 C-Brown up the middle for 3 yds" },
    {
      uid: "P029",
      quarter: 2,
      summary: "9 C-Ward pass short left to 84 G-Helm for 7 yds",
    },
    {
      uid: "P030",
      quarter: 2,
      summary: "30 C-Brown off right tackle for 5 yds",
    },
    {
      uid: "P031",
      quarter: 2,
      summary: "9 C-Ward pass short right to 1 J-Chase for 11 yds",
    },
    { uid: "P032", quarter: 2, summary: "30 C-Brown left guard for 4 yds" },
    {
      uid: "P033",
      quarter: 2,
      summary: "9 C-Ward pass deep left to 5 T-Higgins for 19 yds",
    },
    { uid: "P034", quarter: 2, summary: "9 C-Ward scrambles right for 6 yds" },
  ],
  P047: [
    {
      uid: "P043",
      quarter: 2,
      summary: "16 J-Flacco pass short right to 84 J-Conklin for 8 yds",
    },
    { uid: "P044", quarter: 2, summary: "20 B-Hall up the middle for 3 yds" },
    {
      uid: "P045",
      quarter: 2,
      summary: "16 J-Flacco pass incomplete deep left",
    },
    { uid: "P046", quarter: 2, summary: "20 B-Hall up the middle for 5 yds" },
    {
      uid: "P047",
      quarter: 2,
      summary: "16 J-Flacco pass short middle to 10 A-Lazard for 7 yds",
    },
    {
      uid: "P048",
      quarter: 2,
      summary: "16 J-Flacco pass short left to 20 B-Hall for 4 yds",
    },
    { uid: "P049", quarter: 2, summary: "20 B-Hall off left tackle for 9 yds" },
    { uid: "P050", quarter: 2, summary: "16 J-Flacco spikes the ball" },
  ],
  P063: [
    { uid: "P059", quarter: 3, summary: "30 C-Brown up the middle for 2 yds" },
    {
      uid: "P060",
      quarter: 3,
      summary: "9 C-Ward pass short right to 1 J-Chase for 8 yds",
    },
    {
      uid: "P061",
      quarter: 3,
      summary: "9 C-Ward pass incomplete to 84 G-Helm",
    },
    { uid: "P062", quarter: 3, summary: "30 C-Brown left guard for 4 yds" },
    {
      uid: "P063",
      quarter: 3,
      summary: "9 C-Ward pass deep left to 5 T-Higgins for 31 yds",
    },
    {
      uid: "P064",
      quarter: 3,
      summary: "9 C-Ward pass incomplete deep right to 1 J-Chase",
    },
    { uid: "P065", quarter: 3, summary: "30 C-Brown up the middle for 3 yds" },
    {
      uid: "P066",
      quarter: 3,
      summary: "9 C-Ward pass short middle to 84 G-Helm for 12 yds",
    },
  ],
  P095: [
    { uid: "P091", quarter: 4, summary: "30 C-Brown up the middle for 2 yds" },
    {
      uid: "P092",
      quarter: 4,
      summary: "9 C-Ward pass short left to 1 J-Chase for 6 yds",
    },
    {
      uid: "P093",
      quarter: 4,
      summary: "9 C-Ward pass incomplete to 5 T-Higgins",
    },
    {
      uid: "P094",
      quarter: 4,
      summary: "30 C-Brown off right tackle for 4 yds",
    },
    {
      uid: "P095",
      quarter: 4,
      summary: "30 C-Brown off right tackle for 6 yds",
    },
    {
      uid: "P096",
      quarter: 4,
      summary: "9 C-Ward pass short left to 84 G-Helm for 4 yds",
    },
    {
      uid: "P097",
      quarter: 4,
      summary: "9 C-Ward pass short right to 1 J-Chase for 9 yds",
    },
    { uid: "P098", quarter: 4, summary: "30 C-Brown up the middle for 1 yd" },
  ],
  P127: [
    {
      uid: "P123",
      quarter: 4,
      summary: "16 J-Flacco pass short middle to 10 A-Lazard for 7 yds",
    },
    { uid: "P124", quarter: 4, summary: "20 B-Hall up the middle for 2 yds" },
    {
      uid: "P125",
      quarter: 4,
      summary: "16 J-Flacco pass incomplete deep right",
    },
    {
      uid: "P126",
      quarter: 4,
      summary: "16 J-Flacco pass short left to 84 J-Conklin for 5 yds",
    },
    {
      uid: "P127",
      quarter: 4,
      summary: "16 J-Flacco pass short right to 84 J-Conklin for 9 yds",
    },
    { uid: "P128", quarter: 4, summary: "20 B-Hall up the middle for 2 yds" },
    { uid: "P129", quarter: 4, summary: "16 J-Flacco spikes the ball" },
    {
      uid: "P130",
      quarter: 4,
      summary: "16 J-Flacco pass deep right to 10 A-Lazard for 19 yds",
    },
  ],
};

/** The 14 anchors the mockup ships already CONFIRMED, uid -> display summary (also lifted
 *  verbatim). Each gets a minimal single-entry `gamebookWindow` (just its own resolved play) —
 *  the real ±4 window for these isn't in either mockup file (only the 5 still-pending anchors'
 *  windows are); Stage 1's real n8n payload supplies the genuine ±4 window for every anchor,
 *  pending or not. Reopening one of these via "Change" in this fixture set will show a
 *  one-row picker rather than a full ±4 window — a known, documented fixture limitation, not a
 *  product gap. */
const CONFIRMED: { uid: string; summary: string; quarter: number }[] = [
  {
    uid: "P008",
    summary: "9 C-Ward pass deep right to 5 T-Higgins for 24 yards",
    quarter: 1,
  },
  { uid: "P015", summary: "30 C-Brown up the middle for 3 yards", quarter: 1 },
  {
    uid: "P023",
    summary: "9 C-Ward pass short middle to 1 J-Chase for 9 yards",
    quarter: 2,
  },
  {
    uid: "P039",
    summary: "16 J-Flacco pass short left to 20 B-Hall for 5 yards",
    quarter: 2,
  },
  { uid: "P055", summary: "9 C-Ward scrambles right for 8 yards", quarter: 2 },
  {
    uid: "P071",
    summary: "30 C-Brown off left tackle for 12 yards",
    quarter: 3,
  },
  {
    uid: "P079",
    summary: "16 J-Flacco pass deep middle to 10 A-Lazard for 22 yards",
    quarter: 3,
  },
  {
    uid: "P087",
    summary: "9 C-Ward pass short right to 84 G-Helm for 6 yards",
    quarter: 3,
  },
  {
    uid: "P103",
    summary: "34 I-Guerendo up the middle for 3 yards",
    quarter: 4,
  },
  {
    uid: "P111",
    summary: "9 C-Ward pass incomplete intended for 1 J-Chase",
    quarter: 4,
  },
  {
    uid: "P119",
    summary: "16 J-Flacco pass short middle to 84 J-Conklin for 14 yards",
    quarter: 4,
  },
  { uid: "P135", summary: "30 C-Brown up the middle for 2 yards", quarter: 4 },
  {
    uid: "P143",
    summary: "9 C-Ward pass short left to 1 J-Chase for 7 yards",
    quarter: 4,
  },
  {
    uid: "P148",
    summary: "16 J-Flacco pass deep right to 10 A-Lazard for 19 yards",
    quarter: 4,
  },
];

const PENDING_UIDS = ["P031", "P047", "P063", "P095", "P127"];

/** Every anchor UID, confirmed + pending, in recording (numeric) order — anchorId is assigned by
 *  this position, 1-indexed (0 is reserved for the Phase A start boundary). */
const ALL_ANCHOR_UIDS = [...CONFIRMED.map((c) => c.uid), ...PENDING_UIDS].sort(
  (a, b) => parseInt(a.slice(1), 10) - parseInt(b.slice(1), 10)
);

function clipAssetIdFor(uid: string): string {
  return `fixture-clip-${uid.toLowerCase()}`;
}

/** `A012_C0007` etc — the reel strip's own clip identity, one number per anchor in reel order,
 *  offset by 7 to roughly match the mockup's own `A012_C0007`.. clip ids. */
function clipNameFor(index: number): string {
  return `A012_C${(index * 4 + 7).toString().padStart(4, "0")}`;
}

const FIXTURE_ANCHORS: AnchorTask[] = ALL_ANCHOR_UIDS.map((uid, index) => {
  const anchorId = index + 1;
  const pending = PENDING_UIDS.includes(uid);
  const confirmedEntry = CONFIRMED.find((c) => c.uid === uid);
  const gamebookWindow: GamebookPlayLite[] = pending
    ? PENDING_WINDOWS[uid]
    : [
        {
          uid,
          quarter: confirmedEntry?.quarter ?? 1,
          summary: confirmedEntry?.summary ?? "",
        },
      ];
  const clipAssetId = clipAssetIdFor(uid);
  return {
    anchorId,
    clipAssetId,
    clipName: clipNameFor(index),
    spotterPickUid: uid,
    gamebookWindow,
    resolvedUid: pending ? null : uid,
    resolvedClipAssetId: pending ? null : clipAssetId,
    resolvedByUser: !pending,
    resolvedSource: pending ? null : "picker",
  };
});

/** The full-game play list for the anchor editor's gamebook band — every play the
 *  two mockups' `WINDOWS`/`CONFIRMED`/`STARTPLAYS` tables mention, deduped by uid and synthesized
 *  up into the full `GamebookPlay` shape the real `getPlaysByGameId` join returns. A documented
 *  fixture limitation, same spirit as `CONFIRMED`'s own doc comment above: this is nowhere near a
 *  real game's ~150 rows (only the ~35 plays either mockup happens to name), enough to exercise
 *  the gamebook band's scrolling/centering/situation/flags rendering in fixture mode without
 *  faking a full game's worth of play-by-play by hand. Down/distance/clock/LOS/score are
 *  deterministic, not lifted from either mockup (neither carries them — see the plan's "unknown to
 *  establish first").
 */
const FIXTURE_GAME_PLAYS: GamebookPlay[] = (() => {
  const byUid = new Map<
    string,
    { uid: string; quarter: number; summary: string }
  >();
  for (const play of FIXTURE_FIRST_TEN_PLAYS) {
    byUid.set(play.uid, play);
  }
  for (const entry of CONFIRMED) {
    byUid.set(entry.uid, entry);
  }
  for (const window of Object.values(PENDING_WINDOWS)) {
    for (const play of window) {
      byUid.set(play.uid, play);
    }
  }
  const sorted = Array.from(byUid.values()).sort(
    (a, b) => parseInt(a.uid.slice(1), 10) - parseInt(b.uid.slice(1), 10)
  );
  const downs = ["1", "2", "3", "4"];
  let away = 0;
  let home = 0;
  return sorted.map((play, i) => {
    const playId = parseInt(play.uid.slice(1), 10);
    // Every 23rd play bumps the offense's score (just enough scored plays to exercise the "TD"
    // flag without hand-authoring a real box score).
    if (playId % 23 === 0) {
      if (playId % 2 === 0) {
        away += 6;
      } else {
        home += 6;
      }
    }
    return {
      uid: play.uid,
      playId,
      quarter: play.quarter,
      gameClock: `${Math.max(0, 14 - (i % 15))}:${(59 - ((i * 7) % 60))
        .toString()
        .padStart(2, "0")}`,
      down: downs[i % 4],
      distanceToGo: `${((i % 9) + 1) * 1}`,
      lineOfScrimmage: `CIN ${20 + ((i * 3) % 60)}`,
      teamOnOffense: i % 2 === 0 ? "CIN" : "NYJ",
      teamOnDefense: i % 2 === 0 ? "NYJ" : "CIN",
      awayScore: away,
      homeScore: home,
      yardsGainedOrLost: null,
      mechanicalSummary: play.summary,
      penaltyType: playId % 17 === 0 ? "Holding" : null,
      penaltyTeam: playId % 17 === 0 ? "CIN" : null,
      offensivePlayElements: null,
      presnapElements: null,
      timeOfDay: "13:00",
      playImportance: playId % 11 === 0 ? "critical" : null,
    };
  });
})();

export const FIXTURE_ANCHOR_JOB: AnchorJob = {
  jobId: "fg_A012",
  status: "needs_confirmation",
  mode: "films_ground",
  ...FILMS_GROUND_REVIEW,
  source: {
    kind: "collection",
    name: "ARRI A012",
    url: "https://app.iconik.io/collection/col_1",
  },
  parentCollectionName: "2026 Preseason",
  gameLabel: "Cincinnati @ NY Jets · PRE01",
  start: {
    assetId: "a012c005",
    uid: "P001",
    summaryText: "First live snap — 9 C-Ward pass to 84 G-Helm, 8 yds",
  },
  firstTenPlays: FIXTURE_FIRST_TEN_PLAYS,
  plays: FIXTURE_GAME_PLAYS,
  totalAnchors: FIXTURE_ANCHORS.length,
  anchors: FIXTURE_ANCHORS,
};

/** A fresh `awaiting_start` job (no `start` set yet, no anchors) — Phase A's own fixture target,
 *  distinct from `FIXTURE_ANCHOR_JOB` (already past Phase A). */
export const FIXTURE_AWAITING_START_JOB: AnchorJob = {
  jobId: "fg_A014",
  status: "awaiting_start",
  mode: "films_ground",
  ...FILMS_GROUND_REVIEW,
  source: {
    kind: "collection",
    name: "ARRI A014",
    url: "https://app.iconik.io/collection/col_2",
  },
  parentCollectionName: "2026 Preseason",
  gameLabel: "Cincinnati @ NY Jets · PRE01",
  start: null,
  firstTenPlays: FIXTURE_FIRST_TEN_PLAYS,
  plays: FIXTURE_GAME_PLAYS,
  totalAnchors: 0,
  anchors: [],
};

/** A `slowmo_band` job, already `needs_confirmation` — Phase B's own fixture target for the band
 *  review surface (`AnchorConfirmContent.tsx`'s `reviewMode === "slowmo_band"` branch). One
 *  already-confirmed highlight + one still pending, so both the summary-row and open-card states
 *  are reachable in fixture mode. `band.startClipAssetId`/`endClipAssetId` reuse the same
 *  fixture-clip naming `clipAssetIdFor` uses below so `useProxyUrl`'s fixture stand-in
 *  (`FIXTURE_PROXY_URL`) resolves them like any other fixture clip. */
const FIXTURE_BAND_ANCHORS: AnchorTask[] = [
  {
    anchorId: 1,
    clipAssetId: "fixture-clip-band-p041",
    clipName: "A012_C0041",
    spotterPickUid: "P041",
    gamebookWindow: [
      {
        uid: "P041",
        quarter: 2,
        summary: "9 C-Ward pass deep left to 5 T-Higgins for 34 yds",
      },
    ],
    resolvedUid: "P041",
    resolvedClipAssetId: "fixture-clip-band-p041",
    resolvedByUser: true,
    resolvedSource: "picker",
  },
  {
    anchorId: 2,
    clipAssetId: "fixture-clip-band-p043",
    clipName: "A012_C0043",
    spotterPickUid: "P043",
    gamebookWindow: [
      {
        uid: "P042",
        quarter: 2,
        summary: "30 C-Brown up the middle for 2 yds",
      },
      {
        uid: "P043",
        quarter: 2,
        summary:
          "9 C-Ward scrambles right for 11 yds, ball spotted at goal line",
      },
      {
        uid: "P044",
        quarter: 2,
        summary: "30 C-Brown up the middle for 1 yd, TOUCHDOWN",
      },
    ],
    resolvedUid: null,
    resolvedClipAssetId: null,
    resolvedByUser: false,
    resolvedSource: null,
  },
];

export const FIXTURE_SLOWMO_BAND_JOB: AnchorJob = {
  jobId: "fg_band_001",
  status: "needs_confirmation",
  mode: "films_ground",
  reviewMode: "slowmo_band",
  band: {
    bandId: "band_001",
    startClipAssetId: "fixture-clip-band-start",
    endClipAssetId: "fixture-clip-band-end",
    // Seth's own naming (Monday 12921838919) — exercised here so the fixture-mode job picker
    // shows a real label, not just a bare "Slow-mo band" badge.
    label: "Slow-mo · 4th quarter (20 clips)",
  },
  source: {
    kind: "collection",
    name: "ARRI A012",
    url: "https://app.iconik.io/collection/col_1",
  },
  parentCollectionName: "2026 Preseason",
  gameLabel: "Cincinnati @ NY Jets · PRE01",
  start: {
    assetId: "a012c005",
    uid: "P001",
    summaryText: "First live snap — 9 C-Ward pass to 84 G-Helm, 8 yds",
  },
  firstTenPlays: FIXTURE_FIRST_TEN_PLAYS,
  plays: FIXTURE_GAME_PLAYS,
  totalAnchors: FIXTURE_BAND_ANCHORS.length,
  anchors: FIXTURE_BAND_ANCHORS,
};

/** A `films_ground` job mid-analysis — `start` already confirmed (Phase A done), `anchors` still
 *  empty (n8n hasn't posted results yet). Exercises the same "analysis underway" pending state
 *  `submitFixtureStart`'s timer passes through, but reachable directly by jobId rather than
 *  waiting out `FIXTURE_ANALYSIS_DURATION_MS` after a Phase A submit. */
export const FIXTURE_ANALYZING_JOB: AnchorJob = {
  jobId: "fg_A013",
  status: "analyzing",
  mode: "films_ground",
  ...FILMS_GROUND_REVIEW,
  source: {
    kind: "collection",
    name: "ARRI A013",
    url: "https://app.iconik.io/collection/col_3",
  },
  parentCollectionName: "2026 Preseason",
  gameLabel: "Cincinnati @ NY Jets · PRE01",
  start: {
    assetId: "a013c005",
    uid: "P001",
    summaryText: "First live snap — 9 C-Ward pass to 84 G-Helm, 8 yds",
  },
  firstTenPlays: FIXTURE_FIRST_TEN_PLAYS,
  plays: FIXTURE_GAME_PLAYS,
  totalAnchors: 0,
  anchors: [],
};

/** Every `FIXTURE_ANCHORS` entry, but fully resolved — a `submitted` job never has an open
 *  (`resolvedByUser: false`) anchor left, unlike `FIXTURE_ANCHOR_JOB`'s deliberate pending/
 *  confirmed mix. */
const FIXTURE_SUBMITTED_ANCHORS: AnchorTask[] = FIXTURE_ANCHORS.map((a) =>
  a.resolvedByUser
    ? a
    : {
        ...a,
        resolvedUid: a.spotterPickUid,
        resolvedClipAssetId: a.clipAssetId,
        resolvedByUser: true,
        resolvedSource: "picker",
      }
);

/** A `films_ground` job already `submitted` — the terminal state `submitFixtureAnchors` flips
 *  `FIXTURE_ANCHOR_JOB` into at runtime, reachable directly to see `AnchorConfirmContent`'s
 *  `SubmitBlock` success banner (and confirm the review carousel itself renders read-only). */
export const FIXTURE_SUBMITTED_JOB: AnchorJob = {
  jobId: "fg_A015",
  status: "submitted",
  mode: "films_ground",
  ...FILMS_GROUND_REVIEW,
  source: {
    kind: "collection",
    name: "ARRI A015",
    url: "https://app.iconik.io/collection/col_5",
  },
  parentCollectionName: "2026 Preseason",
  gameLabel: "Cincinnati @ NY Jets · PRE01",
  start: {
    assetId: "a012c005",
    uid: "P001",
    summaryText: "First live snap — 9 C-Ward pass to 84 G-Helm, 8 yds",
  },
  firstTenPlays: FIXTURE_FIRST_TEN_PLAYS,
  plays: FIXTURE_GAME_PLAYS,
  totalAnchors: FIXTURE_SUBMITTED_ANCHORS.length,
  anchors: FIXTURE_SUBMITTED_ANCHORS,
};

/** Top Films' own Phase A — same start picker as Films Ground, but a `saved_search` source
 *  (Films Ground's own fixtures only ever exercise `collection`) so the source-kind rendering gets
 *  covered too. */
export const FIXTURE_TOP_FILMS_AWAITING_JOB: AnchorJob = {
  jobId: "tf_B001",
  status: "awaiting_start",
  mode: "top_films",
  ...FILMS_GROUND_REVIEW,
  source: {
    kind: "saved_search",
    name: "Top Plays — Week 1",
    url: "https://app.iconik.io/saved-search/ss_1",
  },
  parentCollectionName: null,
  gameLabel: "Cincinnati @ NY Jets · PRE01",
  start: null,
  firstTenPlays: FIXTURE_FIRST_TEN_PLAYS,
  plays: FIXTURE_GAME_PLAYS,
  totalAnchors: 0,
  anchors: [],
};

/** Top Films' terminal state — per `AnchorJobMode`'s own doc comment (`../service.ts`), Top
 *  Films' `POST .../start` goes straight to `submitted` with an empty `anchors[]`, skipping the
 *  other two statuses entirely. Exercises `AnchorReviewPanel`'s "auto-submitted" status card
 *  (no review carousel ever mounts for this job) and the `assets`-kind source rendering. */
export const FIXTURE_TOP_FILMS_SUBMITTED_JOB: AnchorJob = {
  jobId: "tf_B002",
  status: "submitted",
  mode: "top_films",
  ...FILMS_GROUND_REVIEW,
  source: {
    kind: "assets",
    assetCount: 42,
  },
  parentCollectionName: null,
  gameLabel: "Cincinnati @ NY Jets · PRE01",
  start: {
    assetId: "tf002c001",
    uid: "P001",
    summaryText: "(fixture) Top Films start clip",
  },
  firstTenPlays: FIXTURE_FIRST_TEN_PLAYS,
  plays: FIXTURE_GAME_PLAYS,
  totalAnchors: 0,
  anchors: [],
};

export const FIXTURE_PROXY_URL = {
  url: "about:blank",
  expiresAt: new Date(Date.now() + 12 * 60 * 60 * 1000).toISOString(),
};

/** Deterministic hash → hue so two different fixture clips render two visibly different colors
 *  (rather than every card showing the identical placeholder) without pulling in any real image
 *  asset. */
function hashHue(assetId: string): number {
  let hash = 0;
  for (let i = 0; i < assetId.length; i++) {
    hash = (hash * 31 + assetId.charCodeAt(i)) % 360;
  }
  return hash;
}

/** Fixture stand-in for `getKeyframeUrl` (`../service.ts`) — real fixture mode has no iconik
 *  behind it to mint an actual keyframe from, so this renders a small inline SVG "clip still"
 *  instead: a solid color (per-asset, via `hashHue`) with the asset id printed on it, just enough
 *  to exercise `ReelStrip.tsx`'s/`AnchorStage.tsx`'s actual `<img>` rendering path end to end in
 *  fixture mode rather than always falling through to the plain gradient placeholder. */
export function getFixtureKeyframeUrl(assetId: string): {
  url: string;
  expiresAt: string;
} {
  const hue = hashHue(assetId);
  const svg =
    `<svg xmlns="http://www.w3.org/2000/svg" width="160" height="90">` +
    `<rect width="100%" height="100%" fill="hsl(${hue},38%,28%)"/>` +
    `<text x="50%" y="50%" font-family="ui-monospace,monospace" font-size="11" ` +
    `fill="white" text-anchor="middle" dominant-baseline="middle">${assetId}</text>` +
    `</svg>`;
  return {
    url: `data:image/svg+xml;utf8,${encodeURIComponent(svg)}`,
    expiresAt: new Date(Date.now() + 12 * 60 * 60 * 1000).toISOString(),
  };
}

/** Fixture stand-in for the job's own reel (fixtures don't model a bigger collection than its
 *  anchors) — walks `FIXTURE_ANCHORS`' own clip order, close enough to exercise neighbor-nav's
 *  UI in fixture mode without real `gameActionShots` behind it. `showAll` isn't modeled here —
 *  fixture anchors have no `play_visible` distinction to skip. */
export function getFixtureNeighborClip(
  assetId: string,
  direction: "prev" | "next"
): { assetId: string; title: string; url: string; expiresAt: string } | null {
  const idx = FIXTURE_ANCHORS.findIndex((a) => a.clipAssetId === assetId);
  if (idx === -1) {
    return null;
  }
  const neighbor = FIXTURE_ANCHORS[direction === "prev" ? idx - 1 : idx + 1];
  if (!neighbor) {
    return null;
  }
  return {
    assetId: neighbor.clipAssetId,
    title: `(fixture) anchor ${neighbor.anchorId}'s clip`,
    ...FIXTURE_PROXY_URL,
  };
}

/**
 * Mutable in-memory fixture STATE — a module-level `Map`, not the static consts above directly,
 * so `submitFixtureStart` can genuinely flip `awaiting_start -> analyzing -> needs_confirmation`
 * (the latter on a short simulated delay) and `useAnchorJob(jobId, poll)`'s real polling logic has
 * something to actually observe transitioning. Resets on a full page reload — fine for manual
 * fixture-mode QA, not meant to survive across sessions.
 */
const fixtureStore = new Map<string, AnchorJob>([
  [FIXTURE_ANCHOR_JOB.jobId, FIXTURE_ANCHOR_JOB],
  [FIXTURE_AWAITING_START_JOB.jobId, FIXTURE_AWAITING_START_JOB],
  [FIXTURE_SLOWMO_BAND_JOB.jobId, FIXTURE_SLOWMO_BAND_JOB],
  [FIXTURE_ANALYZING_JOB.jobId, FIXTURE_ANALYZING_JOB],
  [FIXTURE_SUBMITTED_JOB.jobId, FIXTURE_SUBMITTED_JOB],
  [FIXTURE_TOP_FILMS_AWAITING_JOB.jobId, FIXTURE_TOP_FILMS_AWAITING_JOB],
  [FIXTURE_TOP_FILMS_SUBMITTED_JOB.jobId, FIXTURE_TOP_FILMS_SUBMITTED_JOB],
]);

/** How long a fixture-mode "analysis" pretends to run before flipping to
 *  `needs_confirmation` — short enough to actually observe in a manual QA session, long enough to
 *  see the "analysis underway" pending state and at least one real poll tick
 *  (`FILMS_GROUND_POLL_INTERVAL_MS` in `../service.ts`) first. */
const FIXTURE_ANALYSIS_DURATION_MS = 15_000;

export function getFixtureJob(jobId: string): AnchorJob | undefined {
  return fixtureStore.get(jobId);
}

export function listFixtureJobSummaries(): AnchorJobSummary[] {
  return Array.from(fixtureStore.values()).map((job) => ({
    jobId: job.jobId,
    status: job.status,
    mode: job.mode,
    source: job.source,
    gameLabel: job.gameLabel,
    parentCollectionName: "2026 Preseason",
    bandLabel: job.band?.label ?? null,
    totalAnchors: job.totalAnchors,
  }));
}

/** Mutates the fixture job to `analyzing`, then — after `FIXTURE_ANALYSIS_DURATION_MS` — to
 *  `needs_confirmation` with `FIXTURE_ANCHOR_JOB`'s own anchor set (the only fixture anchor data
 *  that exists). Mirrors Phase A's real contract (`POST .../start` sets `status -> analyzing`,
 *  fires analysis) closely enough to validate the screen's poll/timeout state machine without a
 *  backend. */
export function submitFixtureStart(
  jobId: string,
  assetId: string,
  uid: string
): void {
  const job = fixtureStore.get(jobId);
  if (!job) {
    return;
  }
  fixtureStore.set(jobId, {
    ...job,
    status: "analyzing",
    start: { assetId, uid, summaryText: "(fixture) start clip confirmed" },
  });
  setTimeout(() => {
    const current = fixtureStore.get(jobId);
    if (!current || current.status !== "analyzing") {
      return; // navigated elsewhere / already resolved — don't clobber a later state
    }
    fixtureStore.set(jobId, {
      ...current,
      status: "needs_confirmation",
      totalAnchors: FIXTURE_ANCHORS.length,
      anchors: FIXTURE_ANCHORS,
    });
  }, FIXTURE_ANALYSIS_DURATION_MS);
}

export function submitFixtureAnchors(jobId: string): AnchorJob | undefined {
  const job = fixtureStore.get(jobId);
  if (!job) {
    return undefined;
  }
  const submitted: AnchorJob = { ...job, status: "submitted" };
  fixtureStore.set(jobId, submitted);
  return submitted;
}
